import React from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../../../i18n/format";
import { LEGAL_PATHS } from "../../../Auth/Legal/legalRules";
import ThemeToggle from "../../../UI/ThemeToggle/ThemeToggle";
import RiseModal from "../../../RiseModal/RiseModal";
import { Link } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";
import {
  IoPersonCircle,
  IoSettings,
  IoLogOutOutline,
  IoPeople,
  IoPhonePortraitOutline,
} from "react-icons/io5";
import { BsShieldLockFill } from "react-icons/bs";
import { BiSolidNotepad } from "react-icons/bi";
import { useLogout } from "../../../../hooks/useLogout";

const ITEM_CLASS =
  "flex items-center gap-2.5 px-4 py-2 text-[13px] text-ui-text-muted no-underline select-none transition-colors hover:bg-ui-row-hover";
const ICON_CLASS = "shrink-0 text-brand";
// The HAJEX Bolt Warehouse phone app's download page for this server. Not
// set: no link (the server has no app to download yet).
const APP_DOWNLOAD_URL = process.env.REACT_APP_APP_DOWNLOAD_URL;

const ProfileModal = ({ onClose, style }) => {
  const user = useSelector((state) => state.user);
  const { t, i18n } = useTranslation(["nav", "common"]);
  const { formatMoney } = useFormat();
  const lang = i18n.resolvedLanguage === "fr" ? "fr" : "en";
  const { logout: handleLogout, loggingOut } = useLogout();

  return (
    <RiseModal style={style} onClose={onClose}>
      <div className="w-56 py-1.5">
        <div className="px-4 pb-1.5 pt-1 text-[11px] font-semibold text-ui-text-muted">
          {t("nav:balance", { amount: formatMoney(user.balance) })}
        </div>

        <Link className={ITEM_CLASS} to="/Profile">
          <IoPersonCircle size={18} className={ICON_CLASS} />
          <span>{t("nav:profile")}</span>
        </Link>
        <Link
          className={ITEM_CLASS}
          to={
            user.role === "Shipper"
              ? "/settings/manage-warehouse"
              : "/settings/manage-permissions"
          }
        >
          <IoSettings size={18} className={ICON_CLASS} />
          <span>{t("nav:settings")}</span>
        </Link>
        {user.role === "Shipper" && !user?.parent_id && (
          <Link className={ITEM_CLASS} to="/settings/manage-helper-users">
            <IoPeople size={18} className={ICON_CLASS} />
            <span>{t("nav:users")}</span>
          </Link>
        )}
        {APP_DOWNLOAD_URL && (
          <a
            className={ITEM_CLASS}
            href={APP_DOWNLOAD_URL}
            target="_blank"
            rel="noreferrer"
          >
            <IoPhonePortraitOutline size={18} className={ICON_CLASS} />
            <span>Get the phone app</span>
          </a>
        )}
        <a
          className={ITEM_CLASS}
          href={LEGAL_PATHS.privacy[lang]}
          target="_blank"
          rel="noreferrer"
        >
          <BsShieldLockFill size={18} className={ICON_CLASS} />
          <span>{t("nav:privacyPolicy")}</span>
        </a>
        <a
          className={ITEM_CLASS}
          href={LEGAL_PATHS.terms[lang]}
          target="_blank"
          rel="noreferrer"
        >
          <BiSolidNotepad size={18} className={ICON_CLASS} />
          <span>{t("nav:terms")}</span>
        </a>

        <div className="my-1.5 border-t border-ui-border" />

        <div className="flex items-center justify-between gap-2 px-4 py-2 text-[13px] text-ui-text-muted select-none">
          <span className="shrink-0 whitespace-nowrap">
            {t("common:theme.label")}
          </span>
          <ThemeToggle compact />
        </div>

        <div className="my-1.5 border-t border-ui-border" />

        <div
          className={`${ITEM_CLASS} cursor-pointer ${
            loggingOut ? "pointer-events-none opacity-60" : ""
          }`}
          onClick={handleLogout}
        >
          <IoLogOutOutline size={18} className={ICON_CLASS} />
          <span className="flex-1">{t("common:actions.logout")}</span>
          {loggingOut ? (
            <CircularProgress size={16} />
          ) : (
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium text-ui-text-subtle shadow-[inset_0_0_15px_-5px_rgba(0,0,0,0.14)]">
              {process.env.React_APP_Version}
            </span>
          )}
        </div>
      </div>
    </RiseModal>
  );
};

export default ProfileModal;
